/**
 * One wall hit per (person, lot, page view) — `analytics.paywallOncePerLot` (Spain).
 *
 * ## Why
 *
 * `paywall_shown` used to fire once per LOCKED BLOCK, on mount: a lot with five
 * locked cards wrote five rows the moment it loaded, whether or not anyone
 * scrolled that far. Measured on milicita.es 20–23.10: 154 events for 42 lot
 * views by 18 people — 3.7 per view, most of them for blocks nobody reached. The
 * owner wants the number to mean «a person hit the wall on a lot».
 *
 * ## What
 *
 * Each locked block registers with the lot's {@link PaywallViewProvider} and
 * watches itself with an IntersectionObserver. The FIRST block that scrolls into
 * view — or is tapped, a tap proves it is on screen — fires the single event:
 *
 *   `paywall_shown { wall, tenderId, sections: [...] }`
 *
 * `wall` is the block reached first (the vocabulary of `PAYWALL_WALLS` is
 * unchanged, so existing series keep their key); `sections` lists every locked
 * block on the lot at that moment, so the per-section detail is not lost. Later
 * blocks of the same page view write nothing. Leaving the lot and coming back is
 * a new page view and counts again.
 *
 * A tap on a padlock is NOT a new event: `upgrade_cta_clicked{source}` already
 * records every padlock / «Desbloquear» tap, with its surface. The tap here only
 * makes sure the impression is on record before that click.
 *
 * Absent the flag (UZ, KZ) every function here is inert and each block keeps
 * emitting its own `paywall_shown` on mount, exactly as before.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { countryConfig } from '../country';
import { track } from './funnel';

// Optional chaining on purpose: unit tests mock `../country` with partial configs.
const ONCE =
  (countryConfig as Partial<typeof countryConfig> | undefined)?.analytics?.paywallOncePerLot ===
  true;

/** Is this deployment counting wall hits per lot view instead of per locked block? */
export function paywallOncePerLot(): boolean {
  return ONCE;
}

export interface PaywallView {
  /** A locked block is on the page. Returns the un-register for its unmount. */
  register(wall: string): () => void;
  /** A locked block was reached (scrolled into view or tapped). Fires at most once. */
  reached(wall: string): void;
}

/** The wall hit of ONE page view of ONE lot. Exported for tests. */
export function makePaywallView(tenderId?: string): PaywallView {
  const live = new Map<string, number>();
  let fired = false;
  return {
    register(wall) {
      live.set(wall, (live.get(wall) ?? 0) + 1);
      return () => {
        const n = (live.get(wall) ?? 1) - 1;
        if (n <= 0) live.delete(wall);
        else live.set(wall, n);
      };
    },
    reached(wall) {
      if (fired) return;
      fired = true;
      const sections = [wall, ...[...live.keys()].filter((w) => w !== wall)];
      track('paywall_shown', { wall, ...(tenderId ? { tenderId } : {}), sections });
    },
  };
}

const PaywallViewContext = createContext<PaywallView | null>(null);

/**
 * Scopes the once-per-view rule to one lot page. Mounted by the tender route with
 * `key={tenderId}`, so a new lot — or the same lot opened again after leaving —
 * starts with a fresh view.
 */
export function PaywallViewProvider({
  tenderId,
  children,
}: {
  tenderId?: string;
  children: ReactNode;
}) {
  const view = useMemo(() => makePaywallView(tenderId), [tenderId]);
  return <PaywallViewContext.Provider value={view}>{children}</PaywallViewContext.Provider>;
}

/** Share of a block (or of the viewport, for a tall one) that counts as «reached». */
const REACHED = 0.4;
const THRESHOLDS = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.75, 1];

/** Did the visitor actually get this block on screen — not just graze its edge? */
export function isReached(entry: {
  isIntersecting: boolean;
  intersectionRatio: number;
  intersectionRect: { height: number };
  rootBounds: { height: number } | null;
}): boolean {
  if (!entry.isIntersecting) return false;
  if (entry.intersectionRatio >= REACHED) return true;
  // A block taller than 2.5 screens can never show 40 % of itself.
  return !!entry.rootBounds && entry.intersectionRect.height >= entry.rootBounds.height * REACHED;
}

/**
 * Wire one locked block into the lot's wall hit. Attach `ref` to the block's root
 * element and call `onTap` from its padlock / CTA `onClick`. `active` is false for
 * a block that is not (or no longer) a wall.
 *
 * Inert unless `analytics.paywallOncePerLot` — callers keep their own on-mount
 * emitter for UZ/KZ behind `!paywallOncePerLot()`.
 */
export function usePaywallBlock(
  wall: string | undefined,
  tenderId: string | undefined,
  active = true,
): { ref: (node: HTMLElement | null) => void; onTap: () => void } {
  const provided = useContext(PaywallViewContext);
  // Outside a lot page (no provider) the block counts itself, once per mount.
  const own = useMemo(() => makePaywallView(tenderId), [tenderId]);
  const view = provided ?? own;
  const [node, setNode] = useState<HTMLElement | null>(null);
  const enabled = ONCE && active && !!wall;

  useEffect(() => {
    if (!enabled || !wall) return;
    return view.register(wall);
  }, [enabled, view, wall]);

  useEffect(() => {
    if (!enabled || !wall || !node) return;
    // No observer (an old WebView, jsdom): the block counts when it is on the page.
    if (typeof IntersectionObserver === 'undefined') {
      view.reached(wall);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some(isReached)) {
          view.reached(wall);
          io.disconnect();
        }
      },
      { threshold: THRESHOLDS },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [enabled, view, wall, node]);

  const onTap = useCallback(() => {
    if (enabled && wall) view.reached(wall);
  }, [enabled, view, wall]);

  return { ref: setNode, onTap };
}
